/** The quick actions every value of a delivery blacklist rule offers — the
 *  grid (whose row click opens the editor) and the rule dialog.
 *
 *  A blacklist rule is defined by what it matches, and the one question it
 *  raises is always "who does this actually hit?". These builders answer it
 *  with the grids that hold the answer: bookings and delivery logs by
 *  destination, the user's own page, the OTA, the tags the condition names.
 *
 *  Two traps:
 *  - `match_type: 'reseller'` stores a reseller TAG NAME (the dialog says so),
 *    not the booking's `reseller_ref`. Linking it to `/bookings?q=reseller=` —
 *    the free-text external ref — would silently return the wrong set, so it
 *    goes to the tag namespaces instead.
 *  - the rules grid has no URL filters at all (its toolbar is component state
 *    and there is no `?edit=`), so nothing here links back into it.
 */
import type { MatchType, UserBrief } from '@/api/blacklists';
import type { WireTagChipRead } from '@/components/TagConditionInput';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingsHref,
  cond,
  deliveryLogsHref,
  idCond,
  notificationsHref,
  tagsHref,
} from '@/utils/gridLinks';

export interface BlacklistCtx {
  /** The rule's own operator — scopes every grid link. Null = a system rule. */
  tenantId?: number | null;
}

const tenantOf = (ctx: BlacklistCtx) => ctx.tenantId ?? null;

/** How many event codes a popover lists before it stops being readable. */
const MAX_EVENT_ACTIONS = 6;

export function ruleTenantActions(tenant: { id: number; code: string }): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.copy(tenant.code, 'Copy operator code'),
  ];
}

/** `match_type: 'user'` — the blocked account itself. */
export function ruleUserActions(user: UserBrief, ctx: BlacklistCtx): ValueAction[] {
  const t = tenantOf(ctx);
  const out: ValueAction[] = [
    act.open(`/users/${user.id}`, 'Open user'),
    act.filter(bookingsHref([idCond('user', user.id)], t), 'Bookings of this user'),
    // The delivery-logs `user` namespace takes an email; an all-digits value
    // falls back to the id server-side.
    act.filter(
      deliveryLogsHref([cond('user', String(user.id))], { tenant: t }),
      'Delivery logs for this user (7 days)',
    ),
  ];
  if (user.email) out.push(act.mailto(user.email), act.copy(user.email, 'Copy email'));
  return out;
}

export function ruleOtaActions(ota: { id: number; name: string }): ValueAction[] {
  return [act.open(`/otas/${ota.id}`, 'Open OTA'), act.copy(ota.name, 'Copy OTA name')];
}

/** The rule's target string, by match type. A regex has no grid that can take
 *  it, so it only copies. */
export function rulePatternActions(
  matchType: MatchType,
  pattern: string,
  ctx: BlacklistCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  switch (matchType) {
    case 'exact':
      return [
        act.copy(pattern, 'Copy pattern'),
        act.mailto(pattern),
        act.filter(bookingsHref([cond('email', pattern)], t), 'Bookings with this email'),
        act.filter(
          deliveryLogsHref([cond('email', pattern)], { tenant: t }),
          'Delivery logs to this address (7 days)',
        ),
      ];
    case 'substring':
      return [
        act.copy(pattern, 'Copy pattern'),
        act.filter(
          bookingsHref([cond('email', pattern, 'icontains')], t),
          'Bookings whose email contains this',
        ),
        act.filter(
          deliveryLogsHref([cond('email', pattern, 'icontains')], { tenant: t }),
          'Delivery logs whose address contains this (7 days)',
        ),
      ];
    case 'reseller':
      // A reseller rule names a TAG, not the booking's reseller ref.
      return [
        act.copy(pattern, 'Copy reseller tag name'),
        act.filter(tagsHref([cond('name', pattern)], t), 'Tags with this name'),
        act.filter(bookingsHref([cond('tag', pattern)], t), 'Bookings tagged with this reseller'),
      ];
    default:
      // regex (and the pattern-less user / ota / tag types, which never get here)
      return [act.copy(pattern, 'Copy pattern')];
  }
}

/** One chip of a `match_type: 'tag'` condition, read from the row's `tags`. */
export function ruleTagChipActions(entry: WireTagChipRead, ctx: BlacklistCtx): ValueAction[] {
  const t = tenantOf(ctx);
  if ('tag' in entry) {
    const tag = entry.tag;
    return [
      act.open(`/tags/${tag.id}`, 'Open tag'),
      act.open(`/tag-categories/${tag.category.id}`, 'Open category'),
      act.filter(
        bookingsHref([idCond('tag', tag.id)], tag.tenant?.id ?? t),
        'Bookings with this tag',
      ),
      act.copy(tag.name, 'Copy tag name'),
    ];
  }
  if ('canonical' in entry) {
    // `/tag-canonicals` takes no params, so the canonical itself has no page —
    // its category's tags are the closest honest target.
    return [
      act.filter(tagsHref([cond('category', entry.category_code)], t), 'Tags in this category'),
      act.copy(entry.canonical, 'Copy canonical'),
    ];
  }
  return [
    act.filter(
      tagsHref([cond('category', entry.category_code), cond('name', entry.tag_name)], t),
      'Tags matching this chip',
    ),
    act.filter(bookingsHref([cond('tag', entry.tag_name)], t), 'Bookings with a tag of this name'),
    act.copy(entry.tag_name, 'Copy tag name'),
  ];
}

/** The event codes a rule blocks (or exempts). Event GROUPS are deliberately
 *  absent: nothing takes a group id in a URL. */
export function ruleEventActions(codes: string[], ctx: BlacklistCtx): ValueAction[] {
  if (codes.length === 0) return [];
  const t = tenantOf(ctx);
  const out: ValueAction[] = codes
    .slice(0, MAX_EVENT_ACTIONS)
    .map((code) =>
      act.filter(notificationsHref([cond('event', code)], t), `Notifications for "${code}"`),
    );
  out.push(act.copy(codes.join(', '), 'Copy event codes'));
  return out;
}

/** A redirect / reply-to inbox. */
export function redirectEmailActions(email: string, ctx: BlacklistCtx): ValueAction[] {
  return [
    act.mailto(email),
    act.copy(email, 'Copy email'),
    act.filter(
      deliveryLogsHref([cond('email', email)], { tenant: tenantOf(ctx) }),
      'Delivery logs to this address (7 days)',
    ),
  ];
}

/** Whoever created, changed or deleted the rule. */
export function actorActions(user: UserBrief): ValueAction[] {
  const out: ValueAction[] = [act.open(`/users/${user.id}`, 'Open user')];
  if (user.email) out.push(act.mailto(user.email), act.copy(user.email, 'Copy email'));
  return out;
}
